import type { Container } from 'pixi.js';
import type { LayerStackAsset } from '../../layerstack/LayerStackLoader';
import { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import { R2_HEIGHT, R2_WIDTH } from '../R2Renderer';

/**
 * Pooled shots. A projectile is tested at its `front` anchor rather than its
 * box centre, so the impact point is where the art's tip is — mirroring moves
 * that anchor with the raster, so a shot fired left tests its left tip.
 */
const DESPAWN_MARGIN_PX = 48;
const FRONT_ANCHOR = 'front';
const MS_PER_SECOND = 1000;

export interface ProjectileImpact {
  readonly slug: string;
  readonly x: number;
  readonly y: number;
}

export type ProjectileHitTest = (x: number, y: number) => boolean;

interface LiveShot {
  readonly slug: string;
  readonly sprite: LayerStackSprite;
  readonly vx: number;
  x: number;
  y: number;
}

export class Projectiles {
  private readonly assets = new Map<string, LayerStackAsset>();
  private readonly idle = new Map<string, LayerStackSprite[]>();
  private readonly live: LiveShot[] = [];
  private readonly impacts: ProjectileImpact[] = [];
  private readonly tip = { x: 0, y: 0 };

  constructor(private readonly layer: Container) {}

  register(slug: string, asset: LayerStackAsset): void {
    if (this.assets.has(slug)) return;
    this.assets.set(slug, asset);
    this.idle.set(slug, []);
  }

  get liveCount(): number {
    return this.live.length;
  }

  /** (x, y) is the emitter point; the shot is centred on it and faces its travel. */
  fire(slug: string, x: number, y: number, vx: number): void {
    const sprite = this.take(slug);
    if (!sprite) return;
    sprite.visible = true;
    sprite.setFacing(vx >= 0 ? 'right' : 'left');
    sprite.setPulsing(false);
    sprite.setPulsing(true);
    sprite.setPixelPosition(x - sprite.targetWidth / 2, y - sprite.targetHeight / 2);
    this.live.push({ slug, sprite, vx, x, y });
  }

  update(deltaMs: number, hitTest: ProjectileHitTest): readonly ProjectileImpact[] {
    this.impacts.length = 0;
    const seconds = deltaMs / MS_PER_SECOND;
    for (let i = this.live.length - 1; i >= 0; i -= 1) {
      const shot = this.live[i]!;
      shot.sprite.advance(deltaMs);
      shot.x += shot.vx * seconds;
      shot.sprite.setPixelPosition(shot.x - shot.sprite.targetWidth / 2, shot.y - shot.sprite.targetHeight / 2);
      this.readTip(shot);
      if (hitTest(this.tip.x, this.tip.y)) {
        this.impacts.push({ slug: shot.slug, x: this.tip.x, y: this.tip.y });
        this.retire(i, shot);
        continue;
      }
      if (this.inBounds(this.tip.x, this.tip.y)) continue;
      this.retire(i, shot);
    }
    return this.impacts;
  }

  clear(): void {
    for (const shot of this.live) {
      shot.sprite.visible = false;
      this.idle.get(shot.slug)?.push(shot.sprite);
    }
    this.live.length = 0;
    this.impacts.length = 0;
  }

  /**
   * Actors live in flat renderer space, so the anchor's local x is already
   * renderer x. Only x comes from `front`: the authored y is not on the travel
   * axis for every bullet (piercing-bullet's `front` sits in the top-left
   * corner), which would spawn the impact spark off the shot.
   */
  private readTip(shot: LiveShot): void {
    const sprite = shot.sprite;
    this.tip.y = sprite.y + sprite.targetHeight / 2;
    if (!(FRONT_ANCHOR in sprite.manifest.anchors)) {
      this.tip.x = sprite.x + sprite.targetWidth / 2;
      return;
    }
    this.tip.x = sprite.x + sprite.getAnchor(FRONT_ANCHOR).localX;
  }

  private inBounds(x: number, y: number): boolean {
    if (x < -DESPAWN_MARGIN_PX || x > R2_WIDTH + DESPAWN_MARGIN_PX) return false;
    return y >= -DESPAWN_MARGIN_PX && y <= R2_HEIGHT + DESPAWN_MARGIN_PX;
  }

  private retire(index: number, shot: LiveShot): void {
    shot.sprite.visible = false;
    this.live.splice(index, 1);
    this.idle.get(shot.slug)?.push(shot.sprite);
  }

  private take(slug: string): LayerStackSprite | null {
    const asset = this.assets.get(slug);
    if (!asset) return null;
    const parked = this.idle.get(slug)?.pop();
    if (parked) return parked;
    const sprite = new LayerStackSprite(asset);
    this.layer.addChild(sprite);
    return sprite;
  }
}
